import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./ShowSponsorship.css";
import axios from "axios";
import API_URL from "../../../utils/API_URL";
import { useNavigate } from "react-router-dom";
import brandLogo from "../../assets/Logo+-+livvium.png";

const ShowSponsorship = () => {
  const [leadsData, setLeadsData] = useState({
    sponsorships: [],
    page: 1,
    pageSize: 10,
    totalPages: 1,
    total: 0,
  });

  const [uiState, setUiState] = useState({
    loading: true,
    error: null,
    selectedLead: null,
    showDetailModal: false,
    currentPage: 1,
    searchTerm: "",
    sortBy: "createdAt",
    sortOrder: "desc",
    filterBy: {
      brand_type: "",
      business_stage: "",
      timeline: "",
      discovery_call: "",
      interest: "",
      referral_source: "",
      status: "",
      sponsorshipType: "",
      areasOfInnovation: "",
      collaborationTypes: "",
      partnerOfferings: "",
    },
    showFilters: false,
    selectedLeads: new Set(),
    showStats: true,
    dateRange: {
      startDate: "",
      endDate: "",
    },
  });

  const navigate = useNavigate();

  // Build query parameters for backend
  const buildQueryParams = useCallback(
    (page = 1, customFilters = null) => {
      const params = new URLSearchParams();

      // Pagination
      params.append("page", page);
      params.append("pageSize", leadsData.pageSize);

      // Sorting
      params.append("sortBy", uiState.sortBy);
      params.append("sortOrder", uiState.sortOrder);

      // Determine filters and search term
      const filters = customFilters || uiState.filterBy;
      const searchTerm = customFilters?.searchTerm || uiState.searchTerm;
      const dateRange = customFilters?.dateRange || uiState.dateRange;

      // Search term
      if (searchTerm) {
        params.append("search", searchTerm);
      }

      // Dynamic filter keys
      const filterKeys = [
        "brand_type",
        "business_stage",
        "timeline",
        "discovery_call",
        "interest",
        "referral_source",
        "status",
        "sponsorshipType",
        "areasOfInnovation",
        "collaborationTypes",
        "partnerOfferings",
      ];

      filterKeys.forEach((key) => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });

      // Date range
      if (dateRange?.startDate) {
        params.append("startDate", dateRange.startDate);
      }
      if (dateRange?.endDate) {
        params.append("endDate", dateRange.endDate);
      }

      return params.toString();
    },
    [
      leadsData.pageSize,
      uiState.sortBy,
      uiState.sortOrder,
      uiState.filterBy,
      uiState.searchTerm,
      uiState.dateRange,
    ]
  );

  // Fetch leads data with backend filtering
  const fetchLeads = useCallback(
    async (page = 1, customFilters = null) => {
      setUiState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const token = localStorage.getItem("token");
        const queryString = buildQueryParams(page, customFilters);

        const response = await axios.get(
          `${API_URL}/admin/getAllSponsership?${queryString}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.status) {
          setLeadsData(response.data.data);
          setUiState((prev) => ({ ...prev, currentPage: page }));
        }
      } catch (error) {
        if (error.response.status === 401) {
          console.log("=====================");
          navigate("/adminlogin");
        }
        setUiState((prev) => ({
          ...prev,
          error: error.response?.data?.message || "Failed to fetch leads data",
        }));
      } finally {
        setUiState((prev) => ({ ...prev, loading: false }));
      }
    },
    [buildQueryParams]
  );

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchLeads(1);
    }, 500); // 500ms delay for search

    return () => clearTimeout(timeoutId);
  }, [uiState.searchTerm]);

  // Fetch on filter changes
  useEffect(() => {
    fetchLeads(1);
  }, [uiState.filterBy, uiState.sortBy, uiState.sortOrder, uiState.dateRange]);

  // Initial load
  useEffect(() => {
    fetchLeads(1);
  }, []);

  // Statistics - now we only use what's available on current page/filters
  const stats = useMemo(() => {
    const preferences = leadsData.sponsorships || [];
    return {
      total: leadsData.total,
      currentPage: preferences.length,
      fashion: preferences.filter((p) => p.brand_type === "Fashion").length,
      startups: preferences.filter(
        (p) => p.business_stage === "Startup / New Brand"
      ).length,
      asapTimeline: preferences.filter((p) => p.timeline === "ASAP").length,
      discoveryCallYes: preferences.filter((p) => p.discovery_call === "Yes")
        .length,
    };
  }, [leadsData]);

  // Event handlers
  const handleSearch = (e) => {
    setUiState((prev) => ({ ...prev, searchTerm: e.target.value }));
  };

  const handleSort = (field) => {
    setUiState((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder:
        prev.sortBy === field && prev.sortOrder === "desc" ? "asc" : "desc",
    }));
  };

  const handleFilterChange = (filterType, value) => {
    setUiState((prev) => ({
      ...prev,
      filterBy: {
        ...prev.filterBy,
        [filterType]: value,
      },
    }));
  };

  const handleDateRangeChange = (field, value) => {
    setUiState((prev) => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [field]: value,
      },
    }));
  };

  const showLeadDetails = (lead) => {
    setUiState((prev) => ({
      ...prev,
      selectedLead: lead,
      showDetailModal: true,
    }));
  };

  const closeDetailModal = () => {
    setUiState((prev) => ({
      ...prev,
      selectedLead: null,
      showDetailModal: false,
    }));
  };

  const clearAllFilters = () => {
    setUiState((prev) => ({
      ...prev,
      filterBy: {
        brand_type: "",
        business_stage: "",
        timeline: "",
        discovery_call: "",
        interest: "",
        referral_source: "",
        status: "",
        sponsorshipType: "",
        areasOfInnovation: "",
        collaborationTypes: "",
        partnerOfferings: "",
      },
      dateRange: {
        startDate: "",
        endDate: "",
      },
      searchTerm: "",
    }));
  };

  const exportLeads = async () => {
    try {
      const token = localStorage.getItem("token");
      const queryString = buildQueryParams(1, {
        ...uiState.filterBy,
        searchTerm: uiState.searchTerm,
        dateRange: uiState.dateRange,
      });

      const response = await axios.get(
        `${API_URL}/admin/exportSponsership?${queryString}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `partnership_export_${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setUiState((prev) => ({
        ...prev,
        error: "Failed to export data",
      }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getBadgeColor = (type, value) => {
    const colors = {
      brand_type: {
        Fashion: "pink",
        Technology: "blue",
        Healthcare: "green",
      },
      business_stage: {
        "Startup / New Brand": "purple",
        Growing: "orange",
        Established: "green",
      },
      timeline: {
        ASAP: "red",
        "1-3 months": "orange",
        "3-6 months": "yellow",
      },
      discovery_call: {
        Yes: "green",
        No: "red",
      },
      status: {
        pending: "yellow",
        approved: "green",
        rejected: "red",
      },
    };

    return colors[type]?.[value] || "gray";
  };

  // Get unique values for filter dropdowns
  const getUniqueValues = (field) => {
    const preferences = leadsData.sponsorships || [];
    const values = new Set();

    preferences.forEach((pref) => {
      if (Array.isArray(pref[field])) {
        pref[field].forEach((item) => values.add(item));
      } else if (pref[field]) {
        values.add(pref[field]);
      }
    });

    return Array.from(values).sort();
  };

  if (uiState.loading && leadsData.sponsorships.length === 0) {
    return (
      <div className="show_leads_environment">
        <div className="show_leads_loading">
          <div className="show_leads_loader">
            <div className="show_leads_spinner"></div>
            <span>Loading leads data...</span>
          </div>
        </div>
      </div>
    );
  }

  const updateSponsorshipStatus = async (id, status) => {
    setUiState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const token = localStorage.getItem("token");

      // Update sponsorship status
      const response = await axios.patch(
        `${API_URL}/admin/${id}/SponsershipStatus`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status) {
        // Refetch updated data
        await fetchLeads(uiState.currentPage);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        console.log("Unauthorized access — redirecting to login.");
        navigate("/adminlogin");
      }
      setUiState((prev) => ({
        ...prev,
        error: error.response?.data?.message || "Failed to update status",
      }));
    } finally {
      setUiState((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <div className="show_leads_environment">
      {/* Header Section */}
      <div className="show_leads_header">
        <div className="show_leads_title_section">
          <h1 className="show_leads_title">
            <img src={brandLogo} alt="" />
            Partnership Management
          </h1>
          <p className="show_leads_subtitle">
            Manage and analyze customer preferences and leads
          </p>
        </div>

        <div className="show_leads_header_actions">
          <button
            className="show_leads_export_btn"
            onClick={() => navigate("/adminleads")}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              {/* User/People icon */}
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
            </svg>
            Go to Leads
          </button>

          <button
            className="show_leads_refresh_btn"
            onClick={() => fetchLeads(uiState.currentPage)}
            disabled={uiState.loading}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4C8.69 4 5.83 6.22 4.78 9.4L2.74 8.75A10.958 10.958 0 0 1 12 2C15.31 2 18.24 3.65 20.24 6.34L22 4.58V10H16.42L17.65 6.35ZM6.35 17.65A7.958 7.958 0 0 0 12 20C15.31 20 18.17 17.78 19.22 14.6L21.26 15.25A10.958 10.958 0 0 1 12 22C8.69 22 5.76 20.35 3.76 17.66L2 19.42V14H7.58L6.35 17.65Z" />
            </svg>
            Refresh
          </button>

          <button className="show_leads_export_btn" onClick={exportLeads}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            </svg>
            Export
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {/* {uiState.showStats && (
        <div className="show_leads_stats_grid">
          <div className="show_leads_stat_card">
            <div className="show_leads_stat_icon total">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
              </svg>
            </div>
            <div className="show_leads_stat_content">
              <span className="show_leads_stat_value">{stats.total}</span>
              <span className="show_leads_stat_label">Total Leads</span>
            </div>
          </div>

          <div className="show_leads_stat_card">
            <div className="show_leads_stat_icon fashion">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,2L13.09,8.26L22,9L17,14L18.18,22.82L12,19.77L5.82,22.82L7,14L2,9L10.91,8.26L12,2Z" />
              </svg>
            </div>
            <div className="show_leads_stat_content">
              <span className="show_leads_stat_value">{stats.fashion}</span>
              <span className="show_leads_stat_label">Fashion Brands</span>
            </div>
          </div>

          <div className="show_leads_stat_card">
            <div className="show_leads_stat_icon startup">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,6A6,6 0 0,1 18,12A6,6 0 0,1 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6Z" />
              </svg>
            </div>
            <div className="show_leads_stat_content">
              <span className="show_leads_stat_value">{stats.startups}</span>
              <span className="show_leads_stat_label">Startups</span>
            </div>
          </div>

          <div className="show_leads_stat_card">
            <div className="show_leads_stat_icon discovery">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17,12C17,14.42 15.28,16.44 13,16.9V21H11V16.9C8.72,16.44 7,14.42 7,12C7,9.58 8.72,7.56 11,7.1V3H13V7.1C15.28,7.56 17,9.58 17,12M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z" />
              </svg>
            </div>
            <div className="show_leads_stat_content">
              <span className="show_leads_stat_value">
                {stats.discoveryCallYes}
              </span>
              <span className="show_leads_stat_label">Discovery Calls</span>
            </div>
          </div>
        </div>
      )} */}

      {/* Search and Filter Bar */}
      <div className="show_leads_filter_section">
        <div className="show_leads_search_container">
          <div className="show_leads_search_wrapper">
            <svg
              className="show_leads_search_icon"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" />
            </svg>
            <input
              type="text"
              placeholder="Search leads by name, email, company, or phone..."
              value={uiState.searchTerm}
              onChange={handleSearch}
              className="show_leads_search_input"
            />
            {uiState.searchTerm && (
              <button
                className="show_leads_search_clear"
                onClick={() =>
                  setUiState((prev) => ({ ...prev, searchTerm: "" }))
                }
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* <button
          className={`show_leads_filter_toggle ${
            uiState.showFilters ? "active" : ""
          }`}
          onClick={() =>
            setUiState((prev) => ({ ...prev, showFilters: !prev.showFilters }))
          }
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M14,12V19.88C14.04,20.18 13.94,20.5 13.71,20.71C13.32,21.1 12.69,21.1 12.3,20.71L10.29,18.7C10.06,18.47 9.96,18.16 10,17.87V12H9.97L4.21,4.62C3.87,4.19 3.95,3.56 4.38,3.22C4.57,3.08 4.78,3 5,3V3H19V3C19.22,3 19.43,3.08 19.62,3.22C20.05,3.56 20.13,4.19 19.79,4.62L14.03,12H14Z" />
          </svg>
          Filters
        </button> */}

        <div style={{ display: "flex" }}>
          <label
            htmlFor="statusFilter"
            style={{ marginRight: "8px", fontWeight: "500" }}
          >
            Filter by Status:
          </label>
          <select
            id="statusFilter"
            className="form-control show_leads_search_input"
            value={uiState.filterBy.status || "all"}
            onChange={(e) => {
              const selected = e.target.value;
              const newStatus = selected === "all" ? "" : selected;

              const updatedFilterBy = {
                ...uiState.filterBy,
                status: newStatus,
              };

              setUiState((prev) => ({
                ...prev,
                filterBy: updatedFilterBy,
              }));

              fetchLeads(1, updatedFilterBy); // re-fetch with new status filter
            }}
            style={{
              padding: "6px 12px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              fontSize: "14px",
            }}
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* =========== All Filter Tab Advanced Filters ===========*/}
      {/* {uiState.showFilters && (
        <div className="show_leads_advanced_filters">
          <div className="show_leads_filter_row">
            <div className="show_leads_filter_group">
              <label>Brand Type</label>
              <select
                value={uiState.filterBy.brand_type}
                className="form-control"
                onChange={(e) =>
                  handleFilterChange("brand_type", e.target.value)
                }
              >
                <option value="">All Types</option>
                {getUniqueValues("brand_type").map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div className="show_leads_filter_group">
              <label>Business Stage</label>
              <select
                value={uiState.filterBy.business_stage}
                className="form-control"
                onChange={(e) =>
                  handleFilterChange("business_stage", e.target.value)
                }
              >
                <option value="">All Stages</option>
                {getUniqueValues("business_stage").map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div className="show_leads_filter_group">
              <label>Timeline</label>
              <select
                value={uiState.filterBy.timeline}
                className="form-control"
                onChange={(e) => handleFilterChange("timeline", e.target.value)}
              >
                <option value="">All Timelines</option>
                {getUniqueValues("timeline").map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div className="show_leads_filter_group">
              <label>Discovery Call</label>
              <select
                value={uiState.filterBy.discovery_call}
                className="form-control"
                onChange={(e) =>
                  handleFilterChange("discovery_call", e.target.value)
                }
              >
                <option value="">All</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <div className="show_leads_filter_group">
              <label>Interest</label>
              <select
                value={uiState.filterBy.interest}
                className="form-control"
                onChange={(e) => handleFilterChange("interest", e.target.value)}
              >
                <option value="">All Interests</option>
                {getUniqueValues("interest").map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div className="show_leads_filter_group">
              <label>Referral Source</label>
              <select
                className="form-control"
                value={uiState.filterBy.referral_source}
                onChange={(e) =>
                  handleFilterChange("referral_source", e.target.value)
                }
              >
                <option value="">All Sources</option>
                {getUniqueValues("referral_source").map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            <div className="show_leads_filter_group">
              <label>Start Date</label>
              <input
                type="date"
                className="form-control"
                value={uiState.dateRange.startDate}
                onChange={(e) =>
                  handleDateRangeChange("startDate", e.target.value)
                }
              />
            </div>

            <div className="show_leads_filter_group">
              <label>End Date</label>
              <input
                type="date"
                className="form-control"
                value={uiState.dateRange.endDate}
                onChange={(e) =>
                  handleDateRangeChange("endDate", e.target.value)
                }
              />
            </div>

            <button
              className="show_leads_clear_filters"
              onClick={clearAllFilters}
            >
              Clear All
            </button>
          </div>

          <div className="show_leads_filter_row"></div>
        </div>
      )} */}

      {/* Error Message */}
      {uiState.error && (
        <div className="show_leads_error">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
          </svg>
          <span>{uiState.error}</span>
          <button
            onClick={() => setUiState((prev) => ({ ...prev, error: null }))}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
        </div>
      )}

      {/* Results Info */}
      <div className="show_leads_results_info">
        <span className="show_leads_results_count">
          Showing {leadsData.sponsorships.length} of {leadsData.total} leads
          {uiState.loading && (
            <span className="show_leads_loading_text"> (Loading...)</span>
          )}
        </span>

        {uiState.selectedLeads.size > 0 && (
          <div className="show_leads_selection_info">
            <span>{uiState.selectedLeads.size} selected</span>
            <button
              onClick={() =>
                setUiState((prev) => ({ ...prev, selectedLeads: new Set() }))
              }
            >
              Clear selection
            </button>
          </div>
        )}
      </div>

      {/* Leads Table */}
      <div className="show_leads_table_container">
        <table className="show_leads_table">
          <thead>
            <tr>
              <th>Logo</th>
              <th>Name & Role</th>
              <th>Email</th>
              <th>Sponsorship Type</th>
              <th>Status</th>
              <th>Created Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {leadsData.sponsorships.map((lead) => (
              <tr key={lead._id}>
                {/* Logo */}
                <td>
                  {lead.logoUrl ? (
                    <img
                      className="show_leads_avatar"
                      src={lead.logoUrl}
                      alt={lead.brandName}
                      style={{
                        objectFit: "contain",
                        backgroundColor: "transparent",
                      }}
                    />
                  ) : (
                    <div className="show_leads_avatar">
                      {lead.brandName?.charAt(0).toUpperCase()}
                    </div>
                  )}
                </td>

                {/* Name & Role */}
                <td>{lead.fullNameAndRole}</td>

                {/* Email */}
                <td>{lead.email}</td>

                {/* Sponsorship Type */}
                <td>
                  <span
                    className={`show_leads_badge show_leads_badge_${getBadgeColor(
                      "timeline",
                      lead.sponsorshipType
                    )}`}
                  >
                    {lead.sponsorshipType}
                  </span>
                </td>

                {/* Status */}
                <td>
                  <span
                    className={`show_leads_badge show_leads_badge_${getBadgeColor(
                      "status",
                      lead.status
                    )}`}
                  >
                    {lead.status?.toUpperCase()}
                  </span>
                </td>

                {/* Actions */}
                <td>
                  <span className="show_leads_date">
                    {formatDate(lead.createdAt)}
                  </span>
                </td>
                <td>
                  <div style={{ display: "flex", gap: "10px" }}>
                    {/* Pop-up (EYE) Button */}
                    <button
                      className="show_leads_action_btn view"
                      onClick={() => showLeadDetails(lead)}
                      title="View Details"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" />
                      </svg>
                    </button>
                    {/* Approve Button */}
                    <button
                      className="show_leads_action_btn approve"
                      onClick={() =>
                        updateSponsorshipStatus(lead._id, "approved")
                      }
                      title="Approve"
                      style={{ backgroundColor: "#00b20073" }}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" />
                      </svg>
                    </button>

                    {/* Deny Button */}
                    <button
                      className="show_leads_action_btn deny"
                      onClick={() =>
                        updateSponsorshipStatus(lead._id, "rejected")
                      }
                      title="Deny"
                      style={{ backgroundColor: "#ff000059" }}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12Z" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {leadsData.sponsorships.length === 0 && !uiState.loading && (
          <div className="show_leads_empty_state">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12A6,6 0 0,0 12,6Z" />
            </svg>
            <h3>No leads found</h3>
            <p>Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {leadsData.totalPages > 1 && (
        <div className="show_leads_pagination">
          <button
            disabled={uiState.currentPage === 1 || uiState.loading}
            onClick={() => fetchLeads(uiState.currentPage - 1)}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.41,7.41L14,6L8,12L14,18L15.41,16.59L10.83,12L15.41,7.41Z" />
            </svg>
            Previous
          </button>

          <div className="show_leads_page_numbers">
            {[...Array(Math.min(leadsData.totalPages, 10))].map((_, index) => {
              let pageNumber;
              if (leadsData.totalPages <= 10) {
                pageNumber = index + 1;
              } else {
                const current = uiState.currentPage;
                const total = leadsData.totalPages;
                if (current <= 5) {
                  pageNumber = index + 1;
                } else if (current >= total - 4) {
                  pageNumber = total - 9 + index;
                } else {
                  pageNumber = current - 4 + index;
                }
              }

              return (
                <button
                  key={pageNumber}
                  className={uiState.currentPage === pageNumber ? "active" : ""}
                  onClick={() => fetchLeads(pageNumber)}
                  disabled={uiState.loading}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          <button
            disabled={
              uiState.currentPage === leadsData.totalPages || uiState.loading
            }
            onClick={() => fetchLeads(uiState.currentPage + 1)}
          >
            Next
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.59,16.59L10,18L16,12L10,6L8.59,7.41L13.17,12L8.59,16.59Z" />
            </svg>
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {uiState.showDetailModal && uiState.selectedLead && (
        <div className="show_leads_modal_overlay" onClick={closeDetailModal}>
          <div
            className="show_leads_modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="show_leads_modal_header">
              <h2>Partnership Details</h2>
              <button
                className="show_leads_modal_close"
                onClick={closeDetailModal}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                </svg>
              </button>
            </div>

            <div className="show_leads_modal_content">
              <div className="show_leads_modal_section">
                <div className="show_leads_modal_grid">
                  <div className="show_sponsorship_modal_field">
                    <label>Brand</label>
                    <span>{uiState.selectedLead.brandName}</span>
                  </div>
                  <div className="show_sponsorship_modal_field">
                    <label>Website</label>
                    <a
                      href={uiState.selectedLead.website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {uiState.selectedLead.website}
                    </a>
                  </div>
                  <div className="show_sponsorship_modal_field">
                    <label>Worked with LIVVIUM</label>
                    <span>{uiState.selectedLead.workedWithLivviumBefore}</span>
                  </div>
                </div>

                <div className="show_sponsorship_modal_field">
                  <label>Innovation Areas</label>
                  <div className="show_leads_tags">
                    {uiState.selectedLead.areasOfInnovation?.map((item, i) => (
                      <span key={i} className="show_leads_tag">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="show_sponsorship_modal_field">
                  <label>Collaboration Types</label>
                  <div className="show_leads_tags">
                    {uiState.selectedLead.collaborationTypes?.map((item, i) => (
                      <span key={i} className="show_leads_tag">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="show_sponsorship_modal_field">
                  <label>Partner Offerings</label>
                  <div className="show_leads_tags">
                    {uiState.selectedLead.partnerOfferings?.map((item, i) => (
                      <span key={i} className="show_leads_tag">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="show_sponsorship_modal_field">
                  <label>Sponsorship Type</label>
                  <span
                    className={`show_leads_badge show_leads_badge_${getBadgeColor(
                      "timeline",
                      uiState.selectedLead.sponsorshipType
                    )}`}
                  >
                    {uiState.selectedLead.sponsorshipType}
                  </span>
                </div>

                <div className="show_sponsorship_modal_field">
                  <label>Status</label>
                  <span
                    className={`show_leads_badge show_leads_badge_${getBadgeColor(
                      "status",
                      uiState.selectedLead.status
                    )}`}
                  >
                    {uiState.selectedLead.status}
                  </span>
                </div>

                <div className="show_leads_modal_grid">
                  <div className="show_sponsorship_modal_field">
                    <label>Created</label>
                    <span>{formatDate(uiState.selectedLead.createdAt)}</span>
                  </div>
                  <div className="show_sponsorship_modal_field">
                    <label>Last Updated</label>
                    <span>{formatDate(uiState.selectedLead.updatedAt)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowSponsorship;
